import { SingleSelect, Tabs, TabsList } from "@appretio/ui-kit";
import type { ViewState } from "../../savedViews/useViewState";
import { ViewSelector } from "../../savedViews/ViewSelector";
import { isIndexFamilyMode } from "../cubeDataTable/utils/indexUtils";
import type { IntakeSource } from "../cubeDataTable/utils/intakeUtils";
import { IntakeSourceTabs } from "../intakeSourceTabs/IntakeSourceTabs";
import { CalendarViewControls } from "./CalendarViewControls";
import {
  forecastTableControls,
  planningYearSelect
} from "./ForecastTableControls.css";
import {
  type ForecastTableControlOptions,
  type ForecastTableControlValues,
  isEditableCalendarView
} from "./forecastTableControlValues";

export interface ForecastTableControlsBaseProps {
  /**
   * The available options to display and that a user can interact with
   */
  options: ForecastTableControlOptions;
  /**
   * Updater function to update the available options that are displayed
   */
  setOptions: React.Dispatch<React.SetStateAction<ForecastTableControlOptions>>;
  /**
   * The current values of the controls, ie. which one is selected
   */
  values: ForecastTableControlValues;
  /**
   * Updater function to update the values of the controls
   */
  setValues: React.Dispatch<React.SetStateAction<ForecastTableControlValues>>;
  /**
   * Whether the data being displayed in the adjactent grid has intake data present
   */
  hasIntakeData?: boolean;
  /**
   * Whether an intake toggle operation is in progress
   */
  intakeLoading?: boolean;
  /** The intake sources with rows to swap in; a tab outside it is disabled. */
  availableIntakeSources?: IntakeSource[];
  /** The intake tabs shown; every source by default. */
  intakeSources?: readonly IntakeSource[];
  /**
   * The page's Views layer, which owns the row selection. Omitted by
   * surfaces that borrow the ribbon without a place to save a view (the
   * style-option modal's read-only Forecast tab): no picker, the rest of the
   * bar unchanged.
   */
  views?: ViewState;
}

export function ForecastTableControls({
  options,
  setOptions,
  values,
  setValues,
  hasIntakeData,
  availableIntakeSources = [],
  intakeSources,
  intakeLoading,
  views
}: ForecastTableControlsBaseProps) {
  function update<K extends keyof ForecastTableControlValues>(
    key: K,
    value: ForecastTableControlValues[K]
  ) {
    setValues({ ...values, [key]: value });
  }

  return (
    <div className={forecastTableControls}>
      <CalendarViewControls
        options={options}
        setOptions={setOptions}
        values={values}
        setValues={setValues}
      />
      <Tabs
        value={values.weekLabels}
        onValueChange={(value) =>
          update(
            "weekLabels",
            value as ForecastTableControlValues["weekLabels"]
          )
        }
      >
        <TabsList triggers={options.weekLabels} />
      </Tabs>

      <Tabs
        value={values.timePeriod}
        onValueChange={(value) =>
          update(
            "timePeriod",
            value as ForecastTableControlValues["timePeriod"]
          )
        }
      >
        <TabsList triggers={options.timePeriod} />
      </Tabs>

      <Tabs
        value={values.planBy}
        onValueChange={(value) =>
          update("planBy", value as ForecastTableControlValues["planBy"])
        }
      >
        <TabsList triggers={options.planBy} />
      </Tabs>

      <IntakeSourceTabs
        value={hasIntakeData ? values.intake : undefined}
        onChange={(source) => update("intake", source)}
        availableSources={availableIntakeSources}
        sources={intakeSources}
        disabled={
          !(isEditableCalendarView(values.calendarView) && hasIntakeData) ||
          intakeLoading
        }
      />

      <Tabs
        value={values.gridStyleGrouping}
        onValueChange={(value) =>
          update(
            "gridStyleGrouping",
            value as ForecastTableControlValues["gridStyleGrouping"]
          )
        }
      >
        <TabsList triggers={options.gridStyleGrouping} />
      </Tabs>

      {views && <ViewSelector views={views} />}

      {/* Last in the row: it only exists under the index-family modes, and
          anything to its right would jump sideways as the planner switches
          plan-by. */}
      {isIndexFamilyMode(values.planBy) && (
        <div className={planningYearSelect}>
          <SingleSelect
            placeholder="Planning Year"
            options={options.planningYear}
            selectedOption={options.planningYear.find(
              (option) => option.value === values.planningYear
            )}
            onChange={(value) => update("planningYear", value)}
          />
        </div>
      )}
    </div>
  );
}
